import { findDOMNode } from 'react-dom';

/**
 * The majority of this code has been taken from
 * https://github.com/draft-js-plugins/draft-js-plugins/blob/4a0bb4d6d6bacabc8fb1e10d2023319bb3a95ca5/draft-js-mention-plugin/src/index.js
 *
 * Only added support for flipping the popover when it exceeds the bounding box of the relative parent.
 * Also, forcing an update at the next render so that the style updates accordingly.
 */

const getRelativeParent = element => {
  if (!element) {
    return document;
  }

  const position = window.getComputedStyle(element).getPropertyValue('position');
  if (position !== 'static') {
    return element;
  }

  return getRelativeParent(element.parentElement);
};

const positionSuggestions = editorKey => ({ decoratorRect, popover, state, props }) => {
  // In case the popover isn't a DOM element but a component we want to bring the actual DOM element for it.
  const popoverDOM = popover instanceof Element ? popover : findDOMNode(popover);
  const popoverStyle = window.getComputedStyle(popoverDOM);
  const popoverHeight = parseInt(popoverStyle.height);
  const popoverWidth = parseInt(popoverStyle.width);
  const relativeParent = getRelativeParent(popoverDOM.parentElement);
  const relativeRect = {};
  const isReversed = {};

  if (relativeParent !== document) {
    relativeRect.scrollLeft = relativeParent.scrollLeft;
    relativeRect.scrollTop = relativeParent.scrollTop;

    const relativeParentRect = relativeParent.getBoundingClientRect();
    relativeRect.left = decoratorRect.left - relativeParentRect.left;
    relativeRect.top = decoratorRect.bottom - relativeParentRect.top;

    isReversed.top = decoratorRect.bottom + popoverHeight + 5 > relativeParentRect.bottom;
    isReversed.left = decoratorRect.left + popoverWidth + 5 > relativeParentRect.right;
  } else {
    relativeRect.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    relativeRect.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    relativeRect.top = decoratorRect.bottom;
    relativeRect.left = decoratorRect.left;
    isReversed.top = decoratorRect.bottom + popoverHeight + 5 > window.innerHeight;
    isReversed.left = decoratorRect.left + popoverWidth + 5 > window.innerWidth;
  }

  let transformOrigin = '';
  let isRightAligned = false;
  if (isReversed.left) {
    relativeRect.left -= popoverWidth;
    if (relativeRect.left < 0) {
      // In case suggestions list is out of the container - align it to container's right (minus 5 pixels).
      isRightAligned = true;
    }
    transformOrigin = 'calc(100% - 1em)';
  } else {
    transformOrigin = '1em';
  }

  if (isReversed.top) {
    // This is a bit hacky, but that's the only way I could get the actual DOM for the currently active block (decorator).
    // At the point of getting to this function we always have just one search and its key is the decorator key.
    // We take the line height from the decorator node because previously we used the bottom of the decorator,
    // But since this is reversed we need to move the popover a single line upwards.
    // Why don't we take the decoratorDOM.height then?
    // There might be cases where we have multiple lines for each block and in these cases height !== line height.
    // (when the user started mention at the end of the line and the editor wrapped the content to the next line).
    const activeBlockKey = Object.keys(props.store.getAllSearches().toJS())[0];
    const decoratorDOM = relativeParent.querySelector(
      `[data-editor="${editorKey}"] [data-offset-key="${activeBlockKey}"]`
    );
    const contentLineHeight = parseInt(window.getComputedStyle(decoratorDOM).lineHeight, 10);
    relativeRect.top -= popoverHeight + contentLineHeight + 5;
    transformOrigin += ' 100%';
  } else {
    transformOrigin += ' 0%';
  }
  const left = relativeRect.left + relativeRect.scrollLeft;
  const top = relativeRect.top + relativeRect.scrollTop;

  let transform;
  let transition;
  if (state.isActive) {
    if (props.suggestions.size > 0) {
      transform = 'scale(1)';
      transition = 'transform 0.25s cubic-bezier(.3,1.2,.2,1)';
    } else {
      transform = 'scale(0)';
      transition = 'transform 0.35s cubic-bezier(.3,1,.2,1)';
    }
  }

  const result = {
    left: isRightAligned ? '' : `${left}px`,
    right: isRightAligned ? '5px' : '',
    top: `${top}px`,
    transform,
    transformOrigin,
    transition
  };

  // in case this is a simple DOM element we don't need to do anything special,
  // the styling will be set automatically.
  if (!popover.forceUpdate) return result;

  requestAnimationFrame(() => {
    popover.forceUpdate();
  });

  return result;
};

export default positionSuggestions;
